/**
 * FS-488 — calendar dates, not instants.
 *
 * Most of the date fields the APIs send are Postgres `date` columns —
 * daily_calcs.date (chart_data.dates), devices.install_date,
 * devices.filter_start_date, devices.filter_change_date,
 * filter_epochs.effective_date — plus the predicted notice/change dates the
 * pipeline derives from them. They serialize as bare `YYYY-MM-DD`, which names
 * a day on a calendar and carries no time and no zone.
 *
 * `new Date('2026-07-31')` reads that as midnight UTC. Rendering that instant
 * anywhere west of UTC names the previous day, so the console showed July 30
 * for a filter installed July 31 while the numbers beside it stayed right.
 *
 * These helpers read the year/month/day out of the string and never build a
 * Date, so the label is the same day in every zone. Timestamps keep instant
 * formatting — that is what `formatTimestamp` is for.
 */

/** A bare calendar date and nothing else. An ISO *timestamp* deliberately does
 *  not match: it names an instant, and which day it falls on depends on a zone
 *  this module does not have. */
const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export interface CalendarDateParts {
  year: number;
  month: number; // 1-12
  day: number;   // 1-31
}

/** The parts of a bare `YYYY-MM-DD` value, or null for anything else —
 *  a timestamp, an empty value, or a well-shaped impossible month. */
export const parseCalendarDate = (
  value: string | null | undefined,
): CalendarDateParts | null => {
  if (!value) return null;
  const m = CALENDAR_DATE_RE.exec(value);
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  return { year: Number(m[1]), month, day };
};

/** Long form — "August 4, 2026". Anything that is not a calendar date comes
 *  back untouched, so a surprise value shows itself instead of being silently
 *  turned into the wrong day (this is also the app's own _fmtDate contract). */
export const formatCalendarDate = (value: string): string => {
  const parts = parseCalendarDate(value);
  if (!parts) return value;
  return `${MONTHS[parts.month - 1]} ${parts.day}, ${parts.year}`;
};

/** Short form for a chart's day axis — "Aug 4".
 *
 *  A value that is not a calendar date is treated as an instant and formatted
 *  in the viewer's zone, which is what the tick used to do for every value.
 *  The legacy `{ transitions: [{ timestamp }] }` delta shape is the only thing
 *  that reaches this path; nothing in the pipeline produces it today. */
export const formatChartDayLabel = (value: string): string => {
  const parts = parseCalendarDate(value);
  if (parts) return `${MONTHS_SHORT[parts.month - 1]} ${parts.day}`;
  const asInstant = new Date(value);
  if (Number.isNaN(asInstant.getTime())) return value;
  return asInstant.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Whole calendar days from a bare `YYYY-MM-DD` up to today, in the viewer's
 *  own zone. Null for anything that is not a calendar date.
 *
 *  `Date.now() - new Date('2026-07-17')` compares an instant with UTC
 *  midnight and divides, so the answer turns on the time of day. Both ends
 *  are whole days here.
 *
 *  "Today" is the UTC day, not the viewer's, on purpose: the phone's chart
 *  card counts the same number in the chart Lambda, which runs in UTC
 *  (render-analysis-chart/stats.go), and the two screens showing 66 and 67
 *  for the same filter is the disagreement FS-607 round 2 set out to end.
 *  Labelled DATES still render in calendar terms (FS-488) — that is a
 *  different question from how many days have elapsed. */
export const calendarDaysSince = (value: string | null | undefined): number | null => {
  const parts = parseCalendarDate(value);
  if (!parts) return null;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const from = Date.UTC(parts.year, parts.month - 1, parts.day);
  return Math.round((today - from) / (1000 * 60 * 60 * 24));
};
